import { Suspense, useState, Component, ReactNode } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Stars } from '@react-three/drei';
import { motion } from 'framer-motion';
import { RotateCcw, Maximize2, Box } from 'lucide-react';
import { AnimalModel } from './AnimalModel';
import { Button } from '@/components/ui';

interface AnimalViewer3DProps {
  modelUrl: string | null;
  imageUrl: string;
  animalName: string;
  className?: string;
}

/**
 * Simple Error Boundary for 3D Canvas
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  onError?: () => void;
  resetKey?: number;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class SimpleErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(): void {
    this.props.onError?.();
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

/**
 * 3D Viewer loading placeholder
 */
function Loader3D() {
  return (
    <mesh>
      <boxGeometry args={[1, 1, 1]} />
      <meshBasicMaterial color="#7C3AED" wireframe />
    </mesh>
  );
}

/**
 * Fallback image when 3D model fails to load
 */
function ImageFallback({
  imageUrl,
  animalName,
  onRetry
}: {
  imageUrl: string;
  animalName: string;
  onRetry: () => void;
}) {
  return (
    <div className="relative w-full h-full">
      <img
        src={imageUrl}
        alt={animalName}
        className="w-full h-full object-cover rounded-xl"
      />
      <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center rounded-xl">
        <Box className="w-12 h-12 text-gray-400 mb-2" />
        <p className="text-gray-300 text-sm mb-2">3D модель недоступна</p>
        <Button size="sm" variant="ghost" onClick={onRetry}>
          <RotateCcw className="w-4 h-4 mr-1" />
          Попробовать снова
        </Button>
      </div>
    </div>
  );
}

/**
 * 3D Animal Viewer with GLB model support
 */
export function AnimalViewer3D({
  modelUrl,
  imageUrl,
  animalName,
  className = ''
}: AnimalViewer3DProps) {
  const [hasError, setHasError] = useState(false);
  const [autoRotate, setAutoRotate] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const handleRetry = () => {
    setHasError(false);
    setResetKey(k => k + 1);
  };

  // If no model URL or error, show image fallback
  if (!modelUrl || hasError) {
    return (
      <div className={`bg-space-800 rounded-xl overflow-hidden ${className}`}>
        <ImageFallback
          imageUrl={imageUrl}
          animalName={animalName}
          onRetry={handleRetry}
        />
      </div>
    );
  }

  return (
    <div className={`relative bg-space-900 rounded-xl overflow-hidden ${className}`}>
      {/* Controls overlay */}
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        <button
          onClick={() => setAutoRotate(!autoRotate)}
          className={`p-2 rounded-lg transition-colors ${
            autoRotate
              ? 'bg-cosmic-purple/80 text-white'
              : 'bg-space-700/80 text-gray-400 hover:text-white'
          }`}
          title={autoRotate ? 'Остановить вращение' : 'Включить вращение'}
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <button
          onClick={() => setIsFullscreen(!isFullscreen)}
          className="p-2 bg-space-700/80 rounded-lg text-gray-400 hover:text-white transition-colors"
          title="На весь экран"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      {/* 3D Canvas */}
      <SimpleErrorBoundary
        fallback={
          <ImageFallback
            imageUrl={imageUrl}
            animalName={animalName}
            onRetry={handleRetry}
          />
        }
        onError={() => setHasError(true)}
        resetKey={resetKey}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`w-full ${isFullscreen ? 'fixed inset-0 z-50 bg-space-900' : 'h-full'}`}
        >
          {isFullscreen && (
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-space-800/90 rounded-lg text-white hover:bg-space-700 transition-colors"
            >
              ✕
            </button>
          )}

          <Canvas
            key={resetKey}
            camera={{ position: [0, 1.5, 6], fov: 50 }}
            className="touch-none"
          >
            {/* Lighting */}
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} intensity={1} />
            <directionalLight position={[-5, -5, -5]} intensity={0.3} />

            {/* Cosmic background */}
            <Stars
              radius={100}
              depth={50}
              count={1000}
              factor={4}
              saturation={0}
              fade
              speed={0.5}
            />

            {/* Controls */}
            <OrbitControls
              enablePan={false}
              enableZoom={true}
              minDistance={3}
              maxDistance={20}
            />

            {/* Model with suspense */}
            <Suspense fallback={<Loader3D />}>
              <AnimalModel
                url={modelUrl}
                autoRotate={autoRotate}
              />
            </Suspense>
          </Canvas>
        </motion.div>
      </SimpleErrorBoundary>

      {/* Instructions */}
      {!isFullscreen && (
        <div className="absolute bottom-2 left-2 text-xs text-gray-500">
          Используй мышь для вращения и зума
        </div>
      )}
    </div>
  );
}
